import { Switch, Text } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import type { ObjectConfig } from "./types";
import { SIMPLE_OBJECT_DESCRIPTIONS } from "./types";

interface SimpleObjectTabProps {
  object: ObjectConfig;
  integrationName: string;
  onUpdate: (updates: Partial<ObjectConfig>) => void;
}

export const SimpleObjectTab = ({ object, integrationName, onUpdate }: SimpleObjectTabProps) => {
  const desc = SIMPLE_OBJECT_DESCRIPTIONS[object.key] || SIMPLE_OBJECT_DESCRIPTIONS.notes;

  return (
    <div className="space-y-6">
      {/* Enable toggle */}
      <div
        className={`flex items-center gap-3 rounded-lg border border-solid px-4 py-3 ${
          object.enabled
            ? "border-[color-mix(in_srgb,var(--mantine-primary-color-filled),transparent_80%)] bg-[color-mix(in_srgb,var(--mantine-primary-color-filled),transparent_97%)]"
            : "border-[var(--mantine-color-default-border)]"
        }`}
      >
        <Switch
          checked={object.enabled}
          onChange={(event) => onUpdate({ enabled: event.currentTarget.checked })}
          size="sm"
        />
        <Text size="sm">
          {object.enabled ? desc.enabled : desc.disabled}
        </Text>
      </div>

      {/* Description */}
      <Text size="sm" c="dimmed">
        {desc.detail}
      </Text>

      {object.enabled && (
        <>
          {/* Backfill info */}
          <div className="border-l-[3px] border-solid border-l-[var(--mantine-color-blue-4)] bg-[var(--mantine-color-blue-0)] rounded-r-lg px-4 py-3">
            <div className="flex items-start gap-2">
              <IconInfoCircle size={14} className="text-[var(--mantine-color-blue-6)] mt-0.5 flex-shrink-0" />
              <Text size="xs" c="dimmed">
                For newly synced records, we'll automatically backfill up to <strong>12 months</strong> of {object.label.toLowerCase()} history from {integrationName}. Going forward, new {object.label.toLowerCase()} will sync in real-time.
              </Text>
            </div>
          </div>

          {/* Association note */}
          <div className="flex items-start gap-2 rounded-lg bg-[var(--mantine-color-gray-0)] px-3 py-2.5">
            <IconInfoCircle size={14} className="text-[var(--mantine-color-dimmed)] mt-0.5 flex-shrink-0" />
            <Text size="xs" c="dimmed">
              {object.label} are linked to Accounts and Contacts using {integrationName}'s native record associations. Make sure your {object.label.toLowerCase()} are properly associated in {integrationName} for the best experience.
            </Text>
          </div>
        </>
      )}
    </div>
  );
};
